import React, { useState } from 'react'
import { api } from '../api'
import { Category, Tenant } from '../types'

interface Props {
  categories: Category[]
  setCategories?: React.Dispatch<React.SetStateAction<Category[]>>
  onRefresh: () => Promise<void> | void
  tenant?: Tenant | null
}

export function CategoriesView({ categories, setCategories, onRefresh }: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [localHiddenIds, setLocalHiddenIds] = useState<Set<string>>(new Set())

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const resetForm = () => {
    setName('')
    setDescription('')
    setError(null)
  }

  const handleOpenAddModal = () => {
    resetForm()
    setShowAddModal(true)
  }

  const handleOpenEditModal = (c: Category) => {
    setError(null)
    setEditingCategory(c)
    setName(c.name)
    setDescription(c.description || '')
  }

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const created = await api.createCategory({
        name,
        description,
      })
      if (setCategories) {
        setCategories((prev) => [created, ...prev])
      } else {
        onRefresh()
      }
      setShowAddModal(false)
      resetForm()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCategory) return
    setError(null)
    try {
      const updated = await api.updateCategory(editingCategory.id, {
        name,
        description,
      })
      setEditingCategory(null)
      resetForm()
      if (setCategories) {
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      } else {
        onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) return
    const itemToDelete = categories.find((c) => c.id === id)
    const originalIndex = categories.findIndex((c) => c.id === id)

    if (setCategories) {
      setCategories((prev) => prev.filter((c) => c.id !== id))
    }
    setLocalHiddenIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteCategory(id)
    } catch (err: any) {
      if (setCategories && itemToDelete) {
        setCategories((prev) => {
          if (prev.some((c) => c.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const displayCategories = categories.filter((c) => !localHiddenIds.has(c.id))

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Kategoriler</h2>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          + Yeni Kategori Ekle
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Kategori Adı</th>
              <th>Açıklama</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {displayCategories.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center empty-state">
                  Henüz kategori eklenmemiş.
                </td>
              </tr>
            ) : (
              displayCategories.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                  </td>
                  <td className="text-muted">{c.description || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleOpenEditModal(c)}
                      >
                        Düzenle
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteCategory(c.id)}
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: Add Category */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Yeni Kategori Ekle</h3>
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label>Kategori Adı *</label>
                <input
                  required
                  placeholder="Örn: Tekstil"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <input
                  placeholder="Kategori açıklaması"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Category */}
      {editingCategory && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Kategori Düzenle</h3>
            <form onSubmit={handleUpdateCategory}>
              <div className="form-group">
                <label>Kategori Adı *</label>
                <input
                  required
                  placeholder="Örn: Tekstil"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <input
                  placeholder="Kategori açıklaması"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingCategory(null)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
